import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import moment from 'moment-timezone'

// Set default timezone to Eastern Time
const TIMEZONE = 'America/New_York'

const CourtBookingForm = ({ initialSlot, onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [bookingStatus, setBookingStatus] = useState(null) // 'approved' or 'pending'
  const [recurringResult, setRecurringResult] = useState(null)
  const [multiCourtResult, setMultiCourtResult] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: {
      courts: initialSlot?.resourceId ? [initialSlot.resourceId] : [],
      start_date: initialSlot ? moment.tz(initialSlot.start, TIMEZONE).format('YYYY-MM-DD') : '',
      start_time: initialSlot ? moment.tz(initialSlot.start, TIMEZONE).format('HH:mm') : '',
      end_time: initialSlot ? moment.tz(initialSlot.end, TIMEZONE).format('HH:mm') : '',
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
      reason: '',
      is_captain_chair: false,
      is_recurring: false,
      recurrence_pattern: 'weekly',
      recurrence_end_date: ''
    }
  })

  // Watch fields for auto-fill logic
  const startTime = watch('start_time')
  const isCaptainChair = watch('is_captain_chair')
  const isRecurring = watch('is_recurring')
  const selectedCourts = watch('courts') || []

  // Generate time options in 15-minute increments from 7am to 10pm (in Eastern Time)
  const generateTimeOptions = () => {
    const times = []
    // Only allow reservations between 7am (hour 7) and 10pm (hour 22)
    for (let hour = 7; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, '0')
        const minStr = minute.toString().padStart(2, '0')
        const timeValue = `${hourStr}:${minStr}`
        const displayTime = moment.tz(timeValue, 'HH:mm', TIMEZONE).format('h:mm A')
        times.push({ value: timeValue, label: displayTime })
      }
    }
    return times
  }

  const timeOptions = generateTimeOptions()

  // Auto-fill end time to 1 hour after start time (in Eastern Time)
  useEffect(() => {
    if (startTime) {
      const endMoment = moment.tz(startTime, 'HH:mm', TIMEZONE).add(1, 'hours')
      setValue('end_time', endMoment.format('HH:mm'))
    }
  }, [startTime, setValue])

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true)
      setSubmitError(null)
      setRecurringResult(null)
      setMultiCourtResult(null)

      // Parse dates at START OF DAY in Eastern Time to avoid UTC date shift
      const startDate = moment.tz(data.start_date, 'YYYY-MM-DD', TIMEZONE).startOf('day')
      const endDate = moment.tz(data.start_date, 'YYYY-MM-DD', TIMEZONE).startOf('day')

      // Prepare booking data matching the Booking model schema
      const bookingData = {
        facilities: data.courts,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startTime: data.start_time,
        endTime: data.end_time,
        contactName: `${data.contact_first_name} ${data.contact_last_name}`,
        contactEmail: data.contact_email,
        eventType: data.reason,
        numberOfGuests: 1,
        isCaptainChairBooking: data.is_captain_chair,
        liabilityWaiver: {
          agreed: false,
          signedBy: '',
          signedDate: null
        }
      }

      // Add recurrence fields if recurring is selected
      if (data.is_captain_chair && data.is_recurring && data.recurrence_pattern && data.recurrence_end_date) {
        bookingData.recurrencePattern = data.recurrence_pattern
        bookingData.recurrenceEndDate = moment.tz(data.recurrence_end_date, 'YYYY-MM-DD', TIMEZONE).endOf('day').toISOString()
      }

      // Submit to API
      const response = await axios.post('/api/bookings', bookingData)

      // Track recurring result for display
      if (response.data.recurrenceGroupId) {
        setRecurringResult({
          created: response.data.created?.length || 0,
          skipped: response.data.skipped?.length || 0,
          skippedDates: response.data.skipped || []
        })
      }

      // Track multi-court result for display
      if (response.data.bookings && response.data.bookings.length > 0) {
        setMultiCourtResult({
          created: response.data.bookings.length,
          conflicts: response.data.conflicts || []
        })
      }

      // Track booking status (approved vs pending)
      const status = response.data.booking?.status || response.data.bookings?.[0]?.status || 'approved'
      setBookingStatus(status)

      setSubmitSuccess(true)
      reset()

      // Show success message briefly before closing (longer for recurring or pending)
      const delay = response.data.recurrenceGroupId ? 4000 : status === 'pending' ? 4000 : 2000
      setTimeout(() => {
        if (onSuccess) {
          onSuccess()
        }
      }, delay)
    } catch (error) {
      console.error('Error submitting court booking:', error)
      setSubmitError(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to submit court reservation. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    const isPending = bookingStatus === 'pending'

    return (
      <div className="text-center py-12">
        <div className={`inline-flex items-center justify-center w-16 h-16 ${isPending ? 'bg-yellow-100' : 'bg-green-100'} rounded-full mb-4`}>
          {isPending ? (
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {isPending
            ? 'Reservation Pending Approval'
            : recurringResult
              ? 'Recurring Reservation Confirmed!'
              : 'Reservation Confirmed!'
          }
        </h3>
        {isPending ? (
          <div className="text-gray-600 space-y-2">
            <p>
              Your reservation has been submitted but requires admin approval.
            </p>
            <p className="text-sm text-gray-500">
              This usually means your name or email didn't match our membership records.
              Please make sure you're using the same name and email from your membership registration.
            </p>
          </div>
        ) : recurringResult ? (
          <div className="text-gray-600 space-y-2">
            <p>
              <strong>{recurringResult.created}</strong> booking{recurringResult.created !== 1 ? 's' : ''} created and confirmed.
            </p>
            {recurringResult.skipped > 0 && (
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-left max-w-md mx-auto">
                <p className="text-sm text-yellow-800 font-medium">
                  {recurringResult.skipped} date{recurringResult.skipped !== 1 ? 's' : ''} skipped due to conflicts:
                </p>
                <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                  {recurringResult.skippedDates.map((date, i) => (
                    <li key={i}>{moment(date).format('ddd, MMM D, YYYY')}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : multiCourtResult ? (
          <div className="text-gray-600 space-y-2">
            <p>
              <strong>{multiCourtResult.created}</strong> court{multiCourtResult.created !== 1 ? 's' : ''} reserved and confirmed.
            </p>
            {multiCourtResult.conflicts.length > 0 && (
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-left max-w-md mx-auto">
                <p className="text-sm text-yellow-800 font-medium">
                  {multiCourtResult.conflicts.length} court{multiCourtResult.conflicts.length !== 1 ? 's' : ''} skipped due to conflicts:
                </p>
                <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                  {multiCourtResult.conflicts.map((c, i) => (
                    <li key={i}>{c.facilityName}: {c.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-600">
            Your tennis court reservation has been confirmed and is now active on the calendar.
          </p>
        )}
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {submitError && (
        <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Court Selection (multi-select checkboxes) */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Select Court{selectedCourts.length !== 1 ? 's' : ''} <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { value: 'court_1_lower_deck', label: 'Court 1 - Lower Deck Side' },
            { value: 'court_2_lower_creek', label: 'Court 2 - Lower Creek Side' },
            { value: 'court_3_upper_pavilion', label: 'Court 3 - Upper Pavilion Side' },
            { value: 'court_4_upper_creek', label: 'Court 4 - Upper Creek Side' },
          ].map((court) => (
            <label
              key={court.value}
              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                selectedCourts.includes(court.value)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                value={court.value}
                {...register('courts', {
                  validate: (value) => (value && value.length > 0) || 'Please select at least one court'
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">{court.label}</span>
            </label>
          ))}
        </div>
        {errors.courts && (
          <p className="mt-1 text-sm text-red-600">{errors.courts.message}</p>
        )}
      </div>

      {/* Name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact_first_name" className="block text-sm font-semibold text-gray-700 mb-2">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact_first_name"
            {...register('contact_first_name', {
              required: 'First name is required',
              minLength: { value: 2, message: 'First name must be at least 2 characters' }
            })}
            className="input"
            placeholder="First name"
          />
          {errors.contact_first_name && (
            <p className="mt-1 text-sm text-red-600">{errors.contact_first_name.message}</p>
          )}
        </div>
        <div>
          <label htmlFor="contact_last_name" className="block text-sm font-semibold text-gray-700 mb-2">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact_last_name"
            {...register('contact_last_name', {
              required: 'Last name is required',
              minLength: { value: 2, message: 'Last name must be at least 2 characters' }
            })}
            className="input"
            placeholder="Last name"
          />
          {errors.contact_last_name && (
            <p className="mt-1 text-sm text-red-600">{errors.contact_last_name.message}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="contact_email" className="block text-sm font-semibold text-gray-700 mb-2">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="contact_email"
          {...register('contact_email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          })}
          className="input"
          placeholder="john@example.com"
        />
        {errors.contact_email && (
          <p className="mt-1 text-sm text-red-600">{errors.contact_email.message}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Required to track your court reservations and enforce the 2-court limit.
        </p>
      </div>

      {/* Reason */}
      <div>
        <label htmlFor="reason" className="block text-sm font-semibold text-gray-700 mb-2">
          Reason for Reservation <span className="text-red-500">*</span>
        </label>
        <select
          id="reason"
          {...register('reason', { required: 'Reason is required' })}
          className="input"
        >
          <option value="">Select a reason...</option>
          <option value="Member Reservation">Member Reservation</option>
          <option value="T2 Match">T2 Match</option>
          <option value="USTA Practice">USTA Practice</option>
          <option value="Ultimate Match">Ultimate Match</option>
          <option value="Club Tournament">Club Tournament</option>
          <option value="ALTA Practice">ALTA Practice</option>
          <option value="Wine Down Wednesday League">Wine Down Wednesday League</option>
          <option value="USTA Match">USTA Match</option>
          <option value="Tennis Clinic">Tennis Clinic</option>
          <option value="Court Maintenance">Court Maintenance</option>
          <option value="ALTA Match">ALTA Match</option>
          <option value="Club Round Robin Event">Club Round Robin Event</option>
        </select>
        {errors.reason && (
          <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>
        )}
      </div>

      {/* Date */}
      <div>
        <label htmlFor="start_date" className="block text-sm font-semibold text-gray-700 mb-2">
          Date <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          id="start_date"
          {...register('start_date', {
            required: 'Date is required',
            validate: value => {
              const selectedDate = moment.tz(value, TIMEZONE).startOf('day')
              const today = moment.tz(TIMEZONE).startOf('day')
              if (selectedDate.isBefore(today)) return 'Date cannot be in the past'
              if (!isCaptainChair) {
                const maxDate = today.clone().add(14, 'days')
                if (selectedDate.isAfter(maxDate)) {
                  return 'Members can book up to 2 weeks in advance. Check the Captain/Board Member box if applicable.'
                }
              }
              return true
            }
          })}
          className="input"
        />
        {errors.start_date && (
          <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
        )}
      </div>

      {/* Time Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="start_time" className="block text-sm font-semibold text-gray-700 mb-2">
            Start Time <span className="text-red-500">*</span>
          </label>
          <select
            id="start_time"
            {...register('start_time', { required: 'Start time is required' })}
            className="input"
          >
            <option value="">Select time</option>
            {timeOptions.map(time => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </select>
          {errors.start_time && (
            <p className="mt-1 text-sm text-red-600">{errors.start_time.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="end_time" className="block text-sm font-semibold text-gray-700 mb-2">
            End Time <span className="text-red-500">*</span>
          </label>
          <select
            id="end_time"
            {...register('end_time', {
              required: 'End time is required',
              validate: (value) => {
                const startTime = watch('start_time')
                const isCaptain = watch('is_captain_chair')
                if (!startTime) return true

                const start = moment.tz(startTime, 'HH:mm', TIMEZONE)
                const end = moment.tz(value, 'HH:mm', TIMEZONE)

                // Check if end is after start
                if (!end.isAfter(start)) {
                  return 'End time must be after start time'
                }

                // 2.5-hour max for regular members
                if (!isCaptain) {
                  const durationMinutes = end.diff(start, 'minutes')
                  if (durationMinutes > 150) {
                    return 'Regular members can reserve a maximum of 2.5 hours per court. Captains and board members may book longer.'
                  }
                }

                return true
              }
            })}
            className="input"
          >
            <option value="">Select time</option>
            {timeOptions.map(time => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </select>
          {errors.end_time && (
            <p className="mt-1 text-sm text-red-600">{errors.end_time.message}</p>
          )}
          {!errors.end_time && startTime && (
            <p className="mt-1 text-xs text-blue-600">
              Default is 1 hour. Adjust in 15-minute increments{!isCaptainChair ? ' (2.5 hour max)' : ''}.
            </p>
          )}
        </div>
      </div>

      {/* Tennis Captain / Chair Member Checkbox */}
      <div className="border-t pt-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="is_captain_chair"
              {...register('is_captain_chair')}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_captain_chair" className="ml-3 text-sm">
              <span className="font-semibold text-gray-900">I am a Tennis Captain or Board Member</span>
              <p className="text-gray-600 mt-1">
                This allows you to book more than 2 weeks out and create recurring reservations. Your status will be verified against membership records.
              </p>
            </label>
          </div>
        </div>
      </div>

      {/* Recurring Reservation Section (only for Captain/Chair) */}
      {isCaptainChair && (
        <div className="border-t pt-4">
          <div className="bg-purple-50 p-4 rounded-lg space-y-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="is_recurring"
                {...register('is_recurring')}
                className="mt-1 h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="is_recurring" className="ml-3 text-sm">
                <span className="font-semibold text-gray-900">Recurring Reservation</span>
                <p className="text-gray-600 mt-1">
                  Create a series of reservations (e.g., weekly ALTA practice). Verified captains and board members' recurring bookings are auto-approved.
                </p>
              </label>
            </div>

            {isRecurring && (
              <div className="ml-8 space-y-4">
                {/* Recurrence Pattern */}
                <div>
                  <label htmlFor="recurrence_pattern" className="block text-sm font-semibold text-gray-700 mb-2">
                    Repeat Every <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="recurrence_pattern"
                    {...register('recurrence_pattern', {
                      required: isRecurring ? 'Recurrence pattern is required' : false
                    })}
                    className="input"
                  >
                    <option value="daily">Day</option>
                    <option value="weekly">Week</option>
                    <option value="monthly">Month</option>
                  </select>
                  {errors.recurrence_pattern && (
                    <p className="mt-1 text-sm text-red-600">{errors.recurrence_pattern.message}</p>
                  )}
                </div>

                {/* Recurrence End Date */}
                <div>
                  <label htmlFor="recurrence_end_date" className="block text-sm font-semibold text-gray-700 mb-2">
                    Series End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="recurrence_end_date"
                    {...register('recurrence_end_date', {
                      required: isRecurring ? 'End date is required for recurring reservations' : false,
                      validate: value => {
                        if (!isRecurring) return true
                        const startDate = watch('start_date')
                        if (!startDate) return true
                        const start = moment.tz(startDate, TIMEZONE)
                        const end = moment.tz(value, TIMEZONE)
                        if (!end.isAfter(start)) return 'End date must be after start date'
                        return true
                      }
                    })}
                    className="input"
                  />
                  {errors.recurrence_end_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.recurrence_end_date.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Reservations will be created for each occurrence from the start date through this date.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className={`border-l-4 p-4 rounded ${isCaptainChair ? 'bg-yellow-50 border-yellow-600' : 'bg-blue-50 border-blue-600'}`}>
        <p className={`text-sm ${isCaptainChair ? 'text-yellow-800' : 'text-blue-800'}`}>
          {isCaptainChair ? (
            <>
              <strong>Captain/Board Member Booking:</strong> Your status will be verified against membership records. Verified captains and board members may book the courts necessary for practice and matches, create recurring reservations, and book further in advance without requiring approval.
            </>
          ) : (
            <>
              <strong>Note:</strong> Members can have up to 2 court reservations within a 7-day period, book up to 2 weeks in advance, and reserve a maximum of 2.5 hours per court. Your reservation will be confirmed immediately upon submission.
            </>
          )}
        </p>
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn px-6 py-3 border-2 border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg font-medium transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isCaptainChair && isRecurring ? 'Creating Series...' : 'Reserving...'}
            </span>
          ) : (
            isCaptainChair && isRecurring ? 'Reserve Recurring Series' : selectedCourts.length > 1 ? `Reserve ${selectedCourts.length} Courts` : 'Reserve Court'
          )}
        </button>
      </div>
    </form>
  )
}

export default CourtBookingForm
